<script setup lang="ts">
import { ref, computed } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';
import { importErp } from '@/api/CatalogueApi';
import {
  CatalogueItemCategory,
  Currency,
  type CatalogueItemGroup,
  type ErpImportMode,
  type ErpImportTargetField,
  type ErpImportDefaults,
  type ErpImportConfig,
  type ErpImportResultResponse,
  type ErpStockStatus,
} from '@/models/Catalogue';
import { useAlertsStore } from '@/store/AlertsStore';
import { requiredRule, selectFieldRequiredRule } from '@/helpers/FormRules';
import GroupAutocomplete from '@/components/catalogue/GroupAutocomplete.vue';

const { $gettext } = gettext;
const alerts = useAlertsStore();

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const props = defineProps<{
  modelValue: boolean;
  groups: CatalogueItemGroup[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'imported'): void;
}>();

// --- Step management ---

type ImportStep = 'upload' | 'mapping' | 'defaults' | 'preview' | 'results';

const currentStep = ref<ImportStep>('upload');

const stepOrder = computed<ImportStep[]>(() => {
  if (mode.value === 'update') {
    return ['upload', 'mapping', 'preview', 'results'];
  }
  return ['upload', 'mapping', 'defaults', 'preview', 'results'];
});

const currentStepIndex = computed(() =>
  stepOrder.value.indexOf(currentStep.value),
);

const canGoBack = computed(
  () => currentStepIndex.value > 0 && currentStep.value !== 'results',
);

function goNext() {
  const idx = currentStepIndex.value;
  if (idx < stepOrder.value.length - 1) {
    currentStep.value = stepOrder.value[idx + 1];
  }
}

function goBack() {
  const idx = currentStepIndex.value;
  if (idx > 0) {
    currentStep.value = stepOrder.value[idx - 1];
  }
}

// --- State ---

// Step 1: File + Mode
const selectedFile = ref<File | null>(null);
const mode = ref<ErpImportMode>('update');
const isDragging = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

// Step 2: Column mapping
const csvHeaders = ref<string[]>([]);
const columnMappings = ref<
  { source: string; target: ErpImportTargetField | null }[]
>([]);

// Step 3: Defaults
const defaults = ref<ErpImportDefaults>({});

// Step 4/5: Results
const previewResult = ref<ErpImportResultResponse | null>(null);
const importResult = ref<ErpImportResultResponse | null>(null);

// Loading
const isUploading = ref(false);
const isPreviewing = ref(false);

// --- Options ---

const TARGET_FIELDS: {
  value: ErpImportTargetField | null;
  title: string;
}[] = [
  { value: null, title: $gettext('Skip') },
  { value: 'erp_id', title: $gettext('ERP ID') },
  { value: 'name', title: $gettext('Name') },
  { value: 'description', title: $gettext('Description') },
  { value: 'cost', title: $gettext('Cost') },
  { value: 'stock', title: $gettext('Stock Status') },
  { value: 'location', title: $gettext('Location') },
];

function getAvailableTargets(currentIdx: number) {
  const usedByOthers = new Set(
    columnMappings.value
      .filter((col, i) => i !== currentIdx && col.target !== null)
      .map((col) => col.target),
  );
  return TARGET_FIELDS.map((field) => ({
    ...field,
    props: {
      disabled: field.value !== null && usedByOthers.has(field.value),
    },
  }));
}

const MODE_OPTIONS: { value: ErpImportMode; title: string }[] = [
  { value: 'update', title: $gettext('Update existing items') },
  { value: 'create', title: $gettext('Create new items') },
  { value: 'upsert', title: $gettext('Create or update (upsert)') },
];

const STOCK_STATUS_OPTIONS: { value: ErpStockStatus; title: string }[] = [
  { value: 'in_stock', title: $gettext('In Stock') },
  { value: 'to_order', title: $gettext('To Order') },
  { value: 'kanban', title: $gettext('Kanban') },
];

const CURRENCY_OPTIONS = Object.values(Currency);

const CATEGORY_OPTIONS: { value: CatalogueItemCategory; title: string }[] = [
  { value: CatalogueItemCategory.CUSTOM, title: $gettext('Custom') },
  { value: CatalogueItemCategory.STANDARD, title: $gettext('Standard') },
  { value: CatalogueItemCategory.DUMMY, title: $gettext('Dummy') },
];

// --- CSV header parsing ---

function parseCsvHeaders(file: File): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      let text = reader.result as string;
      // Strip BOM
      if (text.charCodeAt(0) === 0xfeff) {
        text = text.slice(1);
      }
      const firstLine = text.split(/\r?\n/)[0] || '';
      // Detect delimiter: if semicolons outnumber commas, use semicolons
      const delimiter =
        (firstLine.match(/;/g) || []).length >
        (firstLine.match(/,/g) || []).length
          ? ';'
          : ',';
      const headers = firstLine
        .split(delimiter)
        .map((h) => h.trim().replace(/^["']|["']$/g, ''));
      resolve(headers.filter((h) => h.length > 0));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file.slice(0, 10240));
  });
}

function autoMapHeaders() {
  const mapping: Record<string, ErpImportTargetField> = {
    erp_id: 'erp_id',
    erpid: 'erp_id',
    sku: 'erp_id',
    artikelnummer: 'erp_id',
    article_number: 'erp_id',
    name: 'name',
    bezeichnung: 'name',
    description: 'description',
    beschreibung: 'description',
    cost: 'cost',
    price: 'cost',
    einkaufspreis: 'cost',
    preis: 'cost',
    stock: 'stock',
    stock_status: 'stock',
    lagerbestand: 'stock',
    location: 'location',
    lagerort: 'location',
  };

  const usedTargets = new Set<string>();
  for (const col of columnMappings.value) {
    const normalized = col.source.toLowerCase().replace(/[\s-]/g, '_');
    const target = mapping[normalized];
    if (target && !usedTargets.has(target)) {
      col.target = target;
      usedTargets.add(target);
    }
  }
}

// --- Validation ---

const mappedTargets = computed(
  () =>
    new Set(
      columnMappings.value
        .filter((m) => m.target !== null)
        .map((m) => m.target),
    ),
);

const mappingErrors = computed<string[]>(() => {
  const errors: string[] = [];
  const m = mode.value;

  if (m === 'update') {
    if (!mappedTargets.value.has('erp_id')) {
      errors.push($gettext('Update mode requires ERP ID mapping.'));
    }
    if (mappedTargets.value.size < 2) {
      errors.push($gettext('Map at least one field besides ERP ID.'));
    }
  } else if (m === 'create') {
    if (!mappedTargets.value.has('name')) {
      errors.push($gettext('Create mode requires Name mapping.'));
    }
    if (!mappedTargets.value.has('cost')) {
      errors.push($gettext('Create mode requires Cost mapping.'));
    }
  } else if (m === 'upsert') {
    if (!mappedTargets.value.has('erp_id')) {
      errors.push($gettext('Upsert mode requires ERP ID mapping.'));
    }
    if (!mappedTargets.value.has('name')) {
      errors.push($gettext('Upsert mode requires Name mapping.'));
    }
    if (!mappedTargets.value.has('cost')) {
      errors.push($gettext('Upsert mode requires Cost mapping.'));
    }
  }

  return errors;
});

const canProceedFromMapping = computed(() => mappingErrors.value.length === 0);

const defaultsValid = computed(() => {
  if (mode.value === 'update') {
    return true;
  }
  return !!defaults.value.category && !!defaults.value.groupUuid;
});

// --- File handling ---

function validateAndSetFile(file: File) {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    alerts.error($gettext('Please select a CSV file.'));
    return;
  }

  if (file.size > MAX_FILE_SIZE) {
    alerts.error($gettext('File exceeds the maximum size of 10 MB.'));
    return;
  }

  selectedFile.value = file;
}

function handleDragOver() {
  isDragging.value = true;
}

function handleDragLeave() {
  isDragging.value = false;
}

function handleDrop(event: DragEvent) {
  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    validateAndSetFile(files[0]);
  }
}

function handleFileSelect(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    validateAndSetFile(target.files[0]);
  }
}

function openFilePicker() {
  fileInput.value?.click();
}

// --- Navigation handlers ---

async function handleProceedToMapping() {
  if (!selectedFile.value) {
    return;
  }

  try {
    const headers = await parseCsvHeaders(selectedFile.value);
    csvHeaders.value = headers;
    columnMappings.value = headers.map((h) => ({ source: h, target: null }));
    autoMapHeaders();
    goNext();
  } catch {
    alerts.error($gettext('Failed to read CSV headers.'));
  }
}

// --- Import config builder ---

function buildImportConfig(): ErpImportConfig {
  return {
    mode: mode.value,
    columnMapping: columnMappings.value
      .filter((m) => m.target !== null)
      .map((m) => ({ source: m.source, target: m.target! })),
    defaults: defaults.value,
  };
}

// --- API calls ---

function handleApiError(error: unknown) {
  const err = error as {
    response?: { status?: number; data?: { detail?: string } };
  };
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;

  if (status === 413) {
    alerts.error(
      $gettext('File exceeds the maximum size allowed by the server.'),
    );
  } else if (status === 422) {
    alerts.error(
      detail
        ? String(detail)
        : $gettext('The file could not be processed. Check the CSV format.'),
    );
  } else {
    alerts.error($gettext('Failed to import file. Please try again.'));
  }
}

async function handleDryRun() {
  if (!selectedFile.value) {
    return;
  }
  isPreviewing.value = true;

  try {
    const config = buildImportConfig();
    const result = await importErp(selectedFile.value, config, true);
    previewResult.value = result;
    goNext();
  } catch (error: unknown) {
    handleApiError(error);
  } finally {
    isPreviewing.value = false;
  }
}

async function handleConfirmImport() {
  if (!selectedFile.value) {
    return;
  }
  isUploading.value = true;

  try {
    const config = buildImportConfig();
    const result = await importErp(selectedFile.value, config, false);
    importResult.value = result;
    currentStep.value = 'results';
    emit('imported');
    alerts.success($gettext('ERP import completed.'));
  } catch (error: unknown) {
    handleApiError(error);
  } finally {
    isUploading.value = false;
  }
}

// --- Results display helpers ---

const displayedResult = computed<ErpImportResultResponse | null>(() => {
  if (currentStep.value === 'preview') {
    return previewResult.value;
  }
  if (currentStep.value === 'results') {
    return importResult.value;
  }
  return null;
});

const updatedItemChangeFields = computed<string[]>(() => {
  const result = displayedResult.value;
  if (!result) {
    return [];
  }
  const fields = new Set<string>();
  for (const item of result.updatedItems) {
    for (const key of Object.keys(item.changes)) {
      fields.add(key);
    }
  }
  return Array.from(fields);
});

const createdItemsHeaders = [
  { title: $gettext('Row'), key: 'rowNumber', align: 'start' as const },
  { title: $gettext('Name'), key: 'name', align: 'start' as const },
  { title: $gettext('ERP ID'), key: 'erpId', align: 'start' as const },
  { title: $gettext('Cost'), key: 'cost', align: 'end' as const },
];

const missingItemsHeaders = [
  { title: $gettext('Row'), key: 'rowNumber', align: 'start' as const },
  { title: $gettext('ERP ID'), key: 'erpId', align: 'start' as const },
];

function formatFieldLabel(field: string): string {
  const labels: Record<string, string> = {
    cost: $gettext('Cost'),
    stock: $gettext('Stock Status'),
    name: $gettext('Name'),
    description: $gettext('Description'),
    location: $gettext('Location'),
  };
  return labels[field] || field;
}

function formatChangeValue(value: string | number | null): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'number') {
    return value.toFixed(2);
  }
  return String(value);
}

function stepLabel(step: ImportStep): string {
  const labels: Record<ImportStep, string> = {
    upload: $gettext('Upload'),
    mapping: $gettext('Mapping'),
    defaults: $gettext('Defaults'),
    preview: $gettext('Preview'),
    results: $gettext('Results'),
  };
  return labels[step];
}

// --- Reset / Close ---

function resetState() {
  currentStep.value = 'upload';
  selectedFile.value = null;
  mode.value = 'update';
  isDragging.value = false;
  csvHeaders.value = [];
  columnMappings.value = [];
  defaults.value = {};
  previewResult.value = null;
  importResult.value = null;
  isUploading.value = false;
  isPreviewing.value = false;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}

function handleClose() {
  emit('update:modelValue', false);
  setTimeout(resetState, 300);
}

function handleImportAnother() {
  resetState();
}
</script>

<template>
  <v-dialog
    :model-value="props.modelValue"
    max-width="1200px"
    scrollable
    content-class="erp-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="d-flex flex-column erp-dialog__card">
      <v-card-title class="d-flex align-center">
        <span>{{ $gettext('ERP Import') }}</span>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" @click="handleClose" />
      </v-card-title>
      <v-divider />

      <!-- Step indicator -->
      <div class="d-flex align-center pa-4 ga-2 flex-wrap">
        <template v-for="(step, idx) in stepOrder" :key="step">
          <v-chip
            :color="idx <= currentStepIndex ? 'primary' : 'grey-lighten-2'"
            :variant="idx === currentStepIndex ? 'flat' : 'tonal'"
            size="small"
          >
            {{ idx + 1 }}. {{ stepLabel(step) }}
          </v-chip>
          <v-icon v-if="idx < stepOrder.length - 1" size="small" color="grey">
            mdi-chevron-right
          </v-icon>
        </template>
      </div>
      <v-divider />

      <v-card-text class="flex-grow-1 overflow-y-auto">
        <!-- STEP 1: Upload + Mode -->
        <template v-if="currentStep === 'upload'">
          <v-select
            v-model="mode"
            :items="MODE_OPTIONS"
            item-title="title"
            item-value="value"
            :label="$gettext('Import Mode')"
            class="mb-4"
            hide-details
          />

          <div
            class="drop-zone d-flex flex-column align-center justify-center pa-8 rounded-lg"
            :class="{ 'drop-zone--active': isDragging }"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
            @click="openFilePicker"
          >
            <v-icon
              icon="mdi-cloud-upload-outline"
              size="48"
              color="grey"
              class="mb-4"
            />

            <template v-if="selectedFile">
              <p class="text-body-1 font-weight-medium">
                {{ selectedFile.name }}
              </p>
              <p class="text-caption text-grey">
                {{ (selectedFile.size / 1024).toFixed(1) }} KB
              </p>
            </template>
            <template v-else>
              <p class="text-body-1">
                {{
                  $gettext('Drag and drop a CSV file here, or click to browse')
                }}
              </p>
              <p class="text-caption text-grey">
                {{ $gettext('CSV files only, max 10 MB') }}
              </p>
            </template>
          </div>

          <input
            ref="fileInput"
            type="file"
            accept=".csv"
            class="d-none"
            @change="handleFileSelect"
          />
        </template>

        <!-- STEP 2: Column Mapping -->
        <template v-if="currentStep === 'mapping'">
          <p class="text-body-2 mb-4">
            {{
              $gettext(
                "Map each CSV column to a target field, or leave as 'Skip'. Only mapped columns are imported.",
              )
            }}
          </p>

          <v-alert
            v-if="mappingErrors.length > 0"
            type="warning"
            variant="tonal"
            class="mb-4"
          >
            <ul class="pl-4">
              <li v-for="(err, i) in mappingErrors" :key="i">{{ err }}</li>
            </ul>
          </v-alert>

          <v-table density="compact">
            <thead>
              <tr>
                <th class="text-left">{{ $gettext('CSV Column') }}</th>
                <th class="text-left">{{ $gettext('Maps To') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(mapping, idx) in columnMappings" :key="idx">
                <td class="font-weight-medium">{{ mapping.source }}</td>
                <td style="width: 300px">
                  <v-select
                    v-model="mapping.target"
                    :items="getAvailableTargets(idx)"
                    item-title="title"
                    item-value="value"
                    density="compact"
                    hide-details
                    variant="outlined"
                  />
                </td>
              </tr>
            </tbody>
          </v-table>
        </template>

        <!-- STEP 3: Defaults (create/upsert only) -->
        <template v-if="currentStep === 'defaults'">
          <p class="text-body-2 mb-4">
            {{ $gettext('Set default values for newly created items.') }}
          </p>

          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="defaults.category"
                :items="CATEGORY_OPTIONS"
                item-title="title"
                item-value="value"
                :label="$gettext('Category')"
                :rules="[requiredRule]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <GroupAutocomplete
                v-model="defaults.groupUuid"
                :groups="props.groups"
                :rules="[selectFieldRequiredRule]"
                required
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="defaults.currency"
                :items="CURRENCY_OPTIONS"
                :label="$gettext('Currency')"
                clearable
                hide-details
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="defaults.stockStatus"
                :items="STOCK_STATUS_OPTIONS"
                item-title="title"
                item-value="value"
                :label="$gettext('Stock Status')"
                clearable
                hide-details
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="defaults.description"
                :label="$gettext('Default Description')"
                hide-details
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="defaults.location"
                :label="$gettext('Default Location')"
                hide-details
              />
            </v-col>
          </v-row>
        </template>

        <!-- STEP 4 & 5: Preview / Results (shared display) -->
        <template v-if="currentStep === 'preview' || currentStep === 'results'">
          <v-alert
            v-if="currentStep === 'preview'"
            type="info"
            variant="tonal"
            class="mb-4"
          >
            {{ $gettext('This is a preview. No changes have been saved yet.') }}
          </v-alert>

          <!-- Summary chips -->
          <v-alert type="success" variant="tonal" class="mb-4">
            <div class="d-flex align-center flex-wrap ga-3">
              <v-chip color="info" variant="flat">
                {{ displayedResult!.totalRowsProcessed }}
                {{ $gettext('rows processed') }}
              </v-chip>
              <v-chip color="success" variant="flat">
                {{ displayedResult!.itemsUpdated }}
                {{ $gettext('updated') }}
              </v-chip>
              <v-chip
                v-if="displayedResult!.itemsCreated > 0"
                color="primary"
                variant="flat"
              >
                {{ displayedResult!.itemsCreated }}
                {{ $gettext('created') }}
              </v-chip>
              <v-chip color="warning" variant="flat">
                {{ displayedResult!.itemsSkipped }}
                {{ $gettext('skipped') }}
              </v-chip>
              <v-chip
                v-if="displayedResult!.missingItems.length > 0"
                color="orange"
                variant="flat"
              >
                {{ displayedResult!.missingItems.length }}
                {{ $gettext('missing') }}
              </v-chip>
              <v-chip
                v-if="displayedResult!.errors.length > 0"
                color="error"
                variant="flat"
              >
                {{ displayedResult!.errors.length }}
                {{ $gettext('errors') }}
              </v-chip>
            </div>
          </v-alert>

          <!-- Updated items (collapsible) -->
          <v-expansion-panels
            v-if="displayedResult!.updatedItems.length > 0"
            class="mb-4"
          >
            <v-expansion-panel>
              <v-expansion-panel-title>
                {{ $gettext('Updated Items') }}
                <v-chip size="small" color="success" class="ml-2">
                  {{ displayedResult!.updatedItems.length }}
                </v-chip>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-table density="compact">
                  <thead>
                    <tr>
                      <th class="text-left">{{ $gettext('Row') }}</th>
                      <th class="text-left">{{ $gettext('Name') }}</th>
                      <th class="text-left">{{ $gettext('ERP ID') }}</th>
                      <th
                        v-for="field in updatedItemChangeFields"
                        :key="field"
                        class="text-left"
                      >
                        {{ formatFieldLabel(field) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in displayedResult!.updatedItems"
                      :key="item.uuid"
                    >
                      <td>{{ item.rowNumber }}</td>
                      <td>{{ item.name }}</td>
                      <td>{{ item.erpId }}</td>
                      <td v-for="field in updatedItemChangeFields" :key="field">
                        <template v-if="item.changes[field]">
                          <span class="text-grey">{{
                            formatChangeValue(item.changes[field].previous)
                          }}</span>
                          <v-icon size="small" class="mx-1">
                            mdi-arrow-right
                          </v-icon>
                          <span class="font-weight-medium">{{
                            formatChangeValue(item.changes[field].new)
                          }}</span>
                        </template>
                        <template v-else>-</template>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Created items (collapsible) -->
          <v-expansion-panels
            v-if="displayedResult!.createdItems.length > 0"
            class="mb-4"
          >
            <v-expansion-panel>
              <v-expansion-panel-title>
                {{ $gettext('Created Items') }}
                <v-chip size="small" color="primary" class="ml-2">
                  {{ displayedResult!.createdItems.length }}
                </v-chip>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-data-table
                  density="compact"
                  items-per-page="10"
                  :items="displayedResult!.createdItems"
                  :headers="createdItemsHeaders"
                  item-value="rowNumber"
                />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Missing items (collapsible) -->
          <v-expansion-panels
            v-if="displayedResult!.missingItems.length > 0"
            class="mb-4"
          >
            <v-expansion-panel>
              <v-expansion-panel-title>
                {{ $gettext('Missing Items') }}
                <v-chip size="small" color="orange" class="ml-2">
                  {{ displayedResult!.missingItems.length }}
                </v-chip>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-data-table
                  density="compact"
                  items-per-page="10"
                  :items="displayedResult!.missingItems"
                  :headers="missingItemsHeaders"
                  item-value="rowNumber"
                />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Errors (collapsible) -->
          <v-expansion-panels
            v-if="displayedResult!.errors.length > 0"
            class="mb-4"
          >
            <v-expansion-panel>
              <v-expansion-panel-title>
                {{ $gettext('Errors') }}
                <v-chip size="small" color="error" class="ml-2">
                  {{ displayedResult!.errors.length }}
                </v-chip>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-list density="compact">
                  <v-list-item
                    v-for="(err, index) in displayedResult!.errors"
                    :key="index"
                    :title="err"
                    prepend-icon="mdi-alert-circle-outline"
                  />
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>
      </v-card-text>

      <v-divider />

      <!-- Navigation buttons -->
      <v-card-actions class="d-flex justify-space-between pa-4">
        <div>
          <r-btn
            v-if="canGoBack"
            variant="outlined"
            icon="mdi-arrow-left"
            @click="goBack"
          >
            {{ $gettext('Back') }}
          </r-btn>
        </div>
        <div class="d-flex ga-2">
          <!-- Step 1: Next -->
          <r-btn
            v-if="currentStep === 'upload'"
            variant="outlined"
            icon="mdi-arrow-right"
            :disabled="!selectedFile"
            @click="handleProceedToMapping"
          >
            {{ $gettext('Next') }}
          </r-btn>

          <!-- Step 2: Next or Preview (for update mode which skips defaults) -->
          <r-btn
            v-if="currentStep === 'mapping' && mode !== 'update'"
            variant="outlined"
            icon="mdi-arrow-right"
            :disabled="!canProceedFromMapping"
            @click="goNext()"
          >
            {{ $gettext('Next') }}
          </r-btn>
          <r-btn
            v-if="currentStep === 'mapping' && mode === 'update'"
            variant="outlined"
            icon="mdi-eye-outline"
            :disabled="!canProceedFromMapping"
            :loading="isPreviewing"
            @click="handleDryRun"
          >
            {{ $gettext('Preview') }}
          </r-btn>

          <!-- Step 3: Preview -->
          <r-btn
            v-if="currentStep === 'defaults'"
            variant="outlined"
            icon="mdi-eye-outline"
            :disabled="!defaultsValid"
            :loading="isPreviewing"
            @click="handleDryRun"
          >
            {{ $gettext('Preview') }}
          </r-btn>

          <!-- Step 4: Confirm Import -->
          <r-btn
            v-if="currentStep === 'preview'"
            variant="outlined"
            icon="mdi-upload"
            :loading="isUploading"
            @click="handleConfirmImport"
          >
            {{ $gettext('Confirm Import') }}
          </r-btn>

          <!-- Step 5: Import Another -->
          <r-btn
            v-if="currentStep === 'results'"
            variant="outlined"
            icon="mdi-refresh"
            @click="handleImportAnother"
          >
            {{ $gettext('Import Another File') }}
          </r-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
:deep(.erp-dialog) {
  max-height: 80vh;
}

.erp-dialog__card {
  max-height: 80vh;
}

.drop-zone {
  border: 2px dashed #bdbdbd;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
  min-height: 160px;

  &:hover,
  &--active {
    border-color: #1976d2;
    background-color: rgba(25, 118, 210, 0.04);
  }
}
</style>
